import { IModelInfo, IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamsKeys,
  EParamType,
  EParamUnit,
  IEditParams
} from '@/editor/types/editParamType';
import { getUniqueId } from '@/editor/utils';
import { getDoorWindowLimitInfo } from './doorWindowParams';
import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { isMultiSelectDoorWindowOnly, isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';

export function getMultiSelectedDoorWindowInfo(multiData: IMultiModelData) {
  const modelsWidth: number[] = [];
  const modelsHeight: number[] = [];
  const modelsOffGround: number[] = [];
  let limitWidth: number = Infinity,
    limitHeight: number = Infinity;

  for (const modelInfo of multiData.modelInfos) {
    const doorWindowData = globalStores.modelStore.getWindowData(modelInfo.id);
    if (!doorWindowData) continue;

    modelsWidth.push(Math.round(doorWindowData.width));
    modelsHeight.push(Math.round(doorWindowData.height));
    modelsOffGround.push(doorWindowData.offGround);
    const { limitHeight: singleLimitHeight, limitWidth: singleLimitWidth } = getDoorWindowLimitInfo(doorWindowData);
    limitHeight = Math.min(limitHeight, singleLimitHeight);
    if (singleLimitWidth) limitWidth = Math.min(limitWidth, singleLimitWidth);
  }

  const isMultiWidth = new Set(modelsWidth).size > 1;
  const isMultiHeight = new Set(modelsHeight).size > 1;
  const isMultiOffGround = new Set(modelsOffGround).size > 1;

  const maxOffGround = isMultiOffGround ? modelsOffGround.sort((a, b) => b - a)[0] : modelsOffGround[0];
  const limitOffGround = globalStores.paramStore.layerHeight - maxOffGround;

  const paramDatas: IEditParams = {
    id: multiData.id,
    type: EOperateMode.MultiSelect,
    title: EParamGroupLabel.WINDOW_MULTI,
    group: [
      {
        id: EParamGroupLabel.SIZE,
        label: EParamGroupLabel.SIZE,
        linkedFields: [EParamsKeys.width, EParamsKeys.height],
        defaultLinked: false,
        fields: [EParamsKeys.width, EParamsKeys.height, EParamsKeys.offGround]
      }
    ],
    values: {
      [EParamsKeys.width]: isMultiWidth ? 0 : modelsWidth[0],
      [EParamsKeys.height]: isMultiHeight ? 0 : modelsHeight[0],
      [EParamsKeys.offGround]: isMultiOffGround ? 0 : modelsOffGround[0]
    },
    fields: {
      [EParamsKeys.width]: {
        type: EParamType.MULTI_NUMBER,
        unit: EParamUnit.MM,
        label: EParamLabel.MODEL_WIDTH,
        settings: {
          isMulti: isMultiWidth,
          readonly: false,
          max: Number.isFinite(limitWidth) ? limitWidth : undefined,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.height]: {
        type: EParamType.MULTI_NUMBER,
        label: EParamLabel.MODEL_HEIGHT,
        unit: EParamUnit.MM,
        settings: {
          isMulti: isMultiHeight,
          readonly: false,
          max: Number.isFinite(limitHeight) ? limitHeight : undefined,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.offGround]: {
        label: EParamLabel.MODEL_OFFGROUND,
        type: EParamType.MULTI_NUMBER,
        unit: EParamUnit.MM,
        settings: {
          isMulti: isMultiOffGround,
          max: limitOffGround,
          readonly: false
        }
      }
    }
  };

  return paramDatas;
}

/** 获取多选构件属性配置 */
/**
 * 多选的参数面板。
 *
 * @param multiSelectingModelInfos 多选进行中的构件列表。传入时按它计算，不传则读已确认的多选数据。
 *   进行中的多选只含家具/组合/隔墙（见 MultiSelect 的 allowMultiSelectedEntityType），不会是纯门窗——
 *   门窗多选来自「选同款」，那条路径直接写 selected，走下面读已确认数据的分支。
 */
export function getMultiSelectedInfo(multiSelectingModelInfos?: IModelInfo[]): IEditParams | undefined {
  if (multiSelectingModelInfos) {
    return isMultiSelectFurnitureOrAssemblyOnly({ modelInfos: multiSelectingModelInfos })
      ? getFurnitureMultiInfo()
      : undefined;
  }

  const selected = globalStores.editorStateStore.selected;
  const multiData = globalStores.getDataByIdType(selected.entityId, EEntityType.MultiModel);
  if (isMultiSelectDoorWindowOnly(multiData)) {
    return getMultiSelectedDoorWindowInfo(multiData);
  }

  // 只有纯家具/组合多选才有共同的属性口径，混进隔墙就整个面板不展示
  if (!isMultiSelectFurnitureOrAssemblyOnly(multiData)) return;

  return getFurnitureMultiInfo();
}

/** 家具/组合多选：只有标题，没有共同可编辑字段。进行中与已完成两条路径共用，保证口径一致 */
function getFurnitureMultiInfo(): IEditParams {
  return {
    id: getUniqueId(), // 这里无意义
    type: EOperateMode.EMPTY,
    title: EParamGroupLabel.FURNITURE_MULTI,
    group: [],
    values: {},
    fields: {}
  };
}
